import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';
import { useNotificationStore } from '@stores/notificationStore';

const ApplicationDetail: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [showNewFlagForm, setShowNewFlagForm] = useState(false);
  const [showEditApp, setShowEditApp] = useState(false);
  const [editAppData, setEditAppData] = useState({ display_name: '', description: '' });
  const [newFlag, setNewFlag] = useState({ flag_key: '', display_name: '', category: 'capability' });
  const [editingFlagKey, setEditingFlagKey] = useState<string | null>(null);
  const [editingFlagData, setEditingFlagData] = useState({ display_name: '', category: 'capability' });
  const [isSavingFlag, setIsSavingFlag] = useState(false);
  const { addNotification } = useNotificationStore();

  const { data: appData, isLoading: appLoading, refetch: refetchApp } = useQuery({
    queryKey: ['app-detail', appId],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/apps/${appId}`);
      return response;
    },
    enabled: !!appId,
  });

  React.useEffect(() => {
    if (appData) {
      setEditAppData({
        display_name: appData.display_name || '',
        description: appData.description || '',
      });
    }
  }, [appData]);

  const { data: flagsData, isLoading: flagsLoading, refetch: refetchFlags } = useQuery({
    queryKey: ['app-flags', appId],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/apps/${appId}/flags`);
      return response;
    },
    enabled: !!appId,
  });

  const handleUpdateApp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.put(`/api/apps/${appId}`, editAppData);
      setShowEditApp(false);
      await refetchApp();
      addNotification({ message: 'Application updated successfully', type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update app';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update app:', error);
    }
  };

  const handleDeleteApp = async () => {
    if (!window.confirm(`Delete the "${appData?.display_name || appId}" application and its flag definitions? Linked installs and claim codes are kept.`)) return;
    try {
      await apiClient.delete(`/api/apps/${appId}`);
      addNotification({ message: 'Application deleted successfully', type: 'success' });
      navigate('/applications');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete app';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to delete app:', error);
    }
  };

  const handleAddFlag = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingFlag(true);
    try {
      await apiClient.post(`/api/apps/${appId}/flags`, newFlag);
      setNewFlag({ flag_key: '', display_name: '', category: 'capability' });
      setShowNewFlagForm(false);
      await refetchFlags();
      addNotification({ message: `Flag ${newFlag.flag_key} added successfully`, type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add flag';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to add flag:', error);
    } finally {
      setIsSavingFlag(false);
    }
  };

  const handleUpdateFlag = async (e: React.FormEvent, flagKey: string) => {
    e.preventDefault();
    setIsSavingFlag(true);
    try {
      const result = await apiClient.put<any>(`/api/apps/${appId}/flags`, {
        flags: [{ flag_key: flagKey, ...editingFlagData }],
      });
      if (result && typeof result.count === 'number' && result.count === 0) {
        throw new Error(`Flag ${flagKey} was rejected (check category and field formats)`);
      }
      setEditingFlagKey(null);
      await refetchFlags();
      addNotification({ message: `Flag ${flagKey} updated successfully`, type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update flag';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update flag:', error);
    } finally {
      setIsSavingFlag(false);
    }
  };

  const handleDeleteFlag = async (flagKey: string) => {
    if (!window.confirm(`Are you sure you want to remove the "${flagKey}" flag?`)) return;
    try {
      await apiClient.delete(`/api/apps/${appId}/flags/${flagKey}`);
      await refetchFlags();
      addNotification({ message: `Flag ${flagKey} removed successfully`, type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete flag';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to delete flag:', error);
    }
  };

  if (appLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading application...</div>
      </div>
    );
  }

  const app = appData;
  const flags = Array.isArray(flagsData) ? flagsData : [];

  return (
    <div>
      <button
        onClick={() => navigate('/applications')}
        className="mb-6 text-blue-600 hover:text-blue-800 underline text-sm"
      >
        ← Back to Applications
      </button>

      <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{app?.display_name}</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditApp(!showEditApp)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              {showEditApp ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={handleDeleteApp}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
            >
              Delete
            </button>
          </div>
        </div>

        {showEditApp ? (
          <form onSubmit={handleUpdateApp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={editAppData.display_name}
                onChange={(e) => setEditAppData({ ...editAppData, display_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editAppData.description}
                onChange={(e) => setEditAppData({ ...editAppData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setShowEditApp(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2 text-gray-600">
            <div>
              <span className="font-semibold">App ID:</span> <code className="bg-gray-100 px-2 py-1 rounded">{app?.app_id}</code>
            </div>
            <div>
              <span className="font-semibold">Category:</span>{' '}
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">{app?.category}</span>
            </div>
            <div>
              <span className="font-semibold">Status:</span>{' '}
              <span className={`px-2 py-1 rounded text-xs font-medium ${app?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {app?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {app?.description && (
              <div>
                <span className="font-semibold">Description:</span> {app.description}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Feature Flags</h2>
          <button
            onClick={() => setShowNewFlagForm(!showNewFlagForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Add Flag
          </button>
        </div>

        {showNewFlagForm && (
          <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
            <h3 className="text-lg font-semibold mb-4">Add Feature Flag</h3>
            <form onSubmit={handleAddFlag} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flag Key</label>
                <input
                  type="text"
                  value={newFlag.flag_key}
                  onChange={(e) => setNewFlag({ ...newFlag, flag_key: e.target.value })}
                  placeholder="e.g., advanced_analytics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={newFlag.display_name}
                  onChange={(e) => setNewFlag({ ...newFlag, display_name: e.target.value })}
                  placeholder="e.g., Advanced Analytics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newFlag.category}
                  onChange={(e) => setNewFlag({ ...newFlag, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="capability">Capability</option>
                  <option value="integration">Integration</option>
                  <option value="compliance">Compliance</option>
                  <option value="beta">Beta</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSavingFlag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                  {isSavingFlag ? 'Saving…' : 'Add Flag'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewFlagForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {flags.length === 0 ? (
          <div className="bg-white rounded-lg p-6 border border-gray-200 text-center text-gray-500">
            No feature flags configured yet. Add one to get started.
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Flag Key</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Display Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((flag: any) => (
                  <React.Fragment key={flag.flag_key}>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      {editingFlagKey === flag.flag_key ? (
                        <>
                          <td colSpan={4} className="px-6 py-4">
                            <form onSubmit={(e) => handleUpdateFlag(e, flag.flag_key)} className="space-y-2">
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Flag Key</label>
                                  <div className="px-3 py-2 bg-gray-100 rounded text-sm">{flag.flag_key}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                                  <input
                                    type="text"
                                    value={editingFlagData.display_name}
                                    onChange={(e) => setEditingFlagData({ ...editingFlagData, display_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                                  <select
                                    value={editingFlagData.category}
                                    onChange={(e) => setEditingFlagData({ ...editingFlagData, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="capability">Capability</option>
                                    <option value="integration">Integration</option>
                                    <option value="compliance">Compliance</option>
                                    <option value="beta">Beta</option>
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <button
                                  type="submit"
                                  disabled={isSavingFlag}
                                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {isSavingFlag ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingFlagKey(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{flag.flag_key}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{flag.display_name}</td>
                          <td className="px-6 py-4 text-sm">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                              {flag.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-right space-x-2">
                            <button
                              onClick={() => {
                                setEditingFlagKey(flag.flag_key);
                                setEditingFlagData({ display_name: flag.display_name, category: flag.category });
                              }}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteFlag(flag.flag_key)}
                              className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                            >
                              Remove
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDetail;
